import React, { useState } from 'react';
import styled from 'styled-components';
import ja from 'date-fns/locale/ja';
import { format } from 'date-fns';

import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { ButtonProps, Typography } from '@mui/material';
import BackspaceRoundedIcon from '@mui/icons-material/BackspaceRounded';
import RequiredLabel from '@ui-elements/RequiredLabel';

import { PALETTE, FONT_SIZES, Z_INDEX } from '~/assets/styles/base';

const DatePickerWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

type StyledTextFieldProps = {
  $width?: string;
};

const StyledTextField = styled(TextField)<StyledTextFieldProps>`
  .MuiOutlinedInput-root {
    background: ${PALETTE.white};
  }
  input[type='text'] {
    padding: 5px 0 5px 11px;
    width: ${({ $width }) => $width};
    background: ${PALETTE.white};
    font-size: ${FONT_SIZES.body2};
    height: 30px;
    color: ${PALETTE.text.main};
  }
`;

type StyledLabelProps = {
  $label?: string;
};

const StyledLabel = styled(Typography)<StyledLabelProps>`
  font-size: ${FONT_SIZES.bodyDefault};
  margin-right: 20px;
  line-height: 40px;
`;

const TextFieldWrapper = styled.div`
  position: relative;
`;

const ClearButton = styled.button`
  position: absolute;
  z-index: ${Z_INDEX.base};
  color: ${PALETTE.gray[500]};
  top: 12px;
  left: 129px;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    opacity: 0.8;
  }
  svg {
    font-size: ${FONT_SIZES.modalInputDefault};
  }
`;

const datePickerStyle = {
  '& .MuiCalendarPicker-root > div:first-of-type': {
    backgroundColor: PALETTE.white,
    mt: 2,
    mb: 0,
    maxHeight: 36,
    minHeight: 36,
    color: PALETTE.white,
    fontSize: FONT_SIZES.body2,
  },
  '& .MuiPickersDay-root.Mui-selected': {
    color: PALETTE.white,
    '&:hover': {
      backgroundColor: PALETTE.primary.main,
    },
    '&:focus': {
      backgroundColor: PALETTE.primary.main,
    },
  },
  '.MuiPickersCalendarHeader-label': {
    color: PALETTE.text.main,
  },
  '.PrivatePickersYear-yearButton.Mui-selected': {
    color: PALETTE.white,
    backgroundColor: PALETTE.primary.main,
  },
  '& .PrivatePickersSlideTransition-root': {
    minHeight: 208,
  },
  '& .MuiPickersCalendarHeader-label': {
    fontSize: FONT_SIZES.body2,
  },
  '& .MuiCalendarPicker-viewTransitionContainer': {
    borderRadius: '0 0 6px 5px',
    borderTop: 0,
    paddingTop: 1.2,
  },
  '& .MuiCalendarPicker-viewTransitionContainer span:not(:first-of-type):not(:last-child)': {
    color: PALETTE.text.main,
  },
  '& .MuiButtonBase-root': {
    fontSize: FONT_SIZES.body3,
    color: PALETTE.text.main,
    '.Mui-selected': {
      backgroundColor: PALETTE.primary.main,
    },
  },
  '& .MuiTypography-root': {
    fontSize: FONT_SIZES.body2,
  },
};

type ScheduleProps = {
  startTime: Date;
  endTime: Date;
};

type Props = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleChange: Function;

  date?: string;
  disabled?: boolean;
  minDate?: string;
  maxDate?: Date;
  forbiddenSchedules?: Array<ScheduleProps>;
  handleClear?: ButtonProps['onClick'];
  label?: string;
  required?: boolean;
  width?: string;
  dataTestId?: string;
};

// // Docs https://mui.com/x/api/date-pickers/desktop-date-picker/
const DatePicker: React.FC<Props & ButtonProps> = ({
  date = '',
  disabled = false,
  minDate,
  maxDate,
  forbiddenSchedules = [],
  handleChange,
  handleClear = null,
  label,
  required,
  width = '150px',
  dataTestId = 'uiParts-DatePicker',
}): JSX.Element => {
  if (ja.options) {
    ja.options.weekStartsOn = 1;
  }

  const [open, setOpen] = useState<boolean>(false);

  const handleOnClick = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={ja}
      dateFormats={{ monthAndYear: 'yyyy年MM月' }}
    >
      <DatePickerWrapper data-testid="uiParts-DatePicker-wrap">
        {required && label ? (
          <StyledLabel $label={label} data-testid="uiParts-InputWithLabel-lbl-req">
            <RequiredLabel />
            {label}
          </StyledLabel>
        ) : (
          <StyledLabel $label={label} data-testid="uiParts-InputWithLabel-lbl-opt">
            {label}
          </StyledLabel>
        )}
        <DesktopDatePicker
          value={date}
          mask=""
          components={{
            OpenPickerIcon: CalendarMonthIcon,
          }}
          inputFormat="yyyy年MM月dd日"
          disabled={disabled}
          minDate={minDate ? new Date(minDate) : undefined}
          maxDate={maxDate || undefined}
          // メンテなどで配信禁止期間がある場合
          shouldDisableDate={(dt) => {
            let disable = false;
            forbiddenSchedules.forEach((forbiddenSchedule) => {
              const start = forbiddenSchedule.startTime;
              const end = forbiddenSchedule.endTime;
              if (start.getDate() < dt.getDate() && dt.getDate() < end.getDate()) {
                disable = true;
              }
            });
            return disable;
          }}
          onChange={(value) => {
            handleChange(
              format(
                /* istanbul ignore next 画面操作上valがnullになることはありえないため */
                value || new Date(),
                'yyyy-MM-dd'
              )
            );
          }}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          PopperProps={{
            sx: datePickerStyle,
          }}
          renderInput={(params) => (
            <TextFieldWrapper>
              {handleClear && (
                <ClearButton onClick={handleClear} data-testid={`${dataTestId}-ClearButton`}>
                  <BackspaceRoundedIcon />
                </ClearButton>
              )}
              <StyledTextField
                {...params}
                onClick={handleOnClick}
                inputProps={{
                  ...params.inputProps,
                  type: 'text',
                  placeholder: '0000年00月00日',
                  readOnly: true,
                  'data-testid': dataTestId,
                }}
                $width={width}
                sx={{
                  svg: { width: '24px', height: '24px' },
                }}
              />
            </TextFieldWrapper>
          )}
        />
      </DatePickerWrapper>
    </LocalizationProvider>
  );
};

export default DatePicker;
